import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatAnnotation } from "~/pages/appointments/hooks/useAgentChat";

/** A message typed while Indy was busy, waiting its turn to be sent. */
export interface QueuedMessage {
  id: number;
  /** The thread it was typed into; it only ever sends there. */
  conversationId: string;
  text: string;
  /** Selections staged when it was queued; they send with it, not later. */
  annotations: ChatAnnotation[];
}

interface UseQueuedMessagesOptions {
  conversationId: string;
  /** True while a send cannot start: streaming, status check, upload. */
  busy: boolean;
  /** Entry being edited; the drain waits rather than send its old text. */
  holdId: number | null;
  /** The composer's send path. Resolves whether the message went out. */
  send: (text: string, annotations: ChatAnnotation[]) => Promise<boolean>;
}

/**
 * Client-side send queue for the composer. The server takes one turn at a
 * time per conversation, so while a reply streams the composer cannot send.
 * Instead of dropping the keystroke, messages typed meanwhile stack here and
 * go out one per turn, in order, once the reply has landed.
 *
 * Queued text is never steered into the running turn — it is a separate,
 * later message, which is why each one can still be edited or removed.
 *
 * Held in component state rather than a module cache: the queue belongs to
 * the surface that shows it, and an unmounted surface has nowhere to show a
 * message it would silently send later.
 */
export function useQueuedMessages({
  conversationId,
  busy,
  holdId,
  send,
}: UseQueuedMessagesOptions) {
  const [queue, setQueue] = useState<QueuedMessage[]>([]);
  const nextIdRef = useRef(1);

  const enqueue = useCallback(
    (text: string, annotations: ChatAnnotation[]) => {
      const id = nextIdRef.current++;
      setQueue((prev) => [...prev, { id, conversationId, text, annotations }]);
    },
    [conversationId],
  );

  const remove = useCallback((id: number) => {
    setQueue((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const update = useCallback((id: number, text: string) => {
    setQueue((prev) => prev.map((m) => (m.id === id ? { ...m, text } : m)));
  }, []);

  /** Drop this conversation's entries; other threads keep theirs. */
  const clear = useCallback(() => {
    setQueue((prev) => prev.filter((m) => m.conversationId !== conversationId));
  }, [conversationId]);

  // Drain one message per idle. Effects run after commit, so by the time this
  // sees `busy` drop the finished reply is already on screen — `done` sets the
  // final message and clears streaming in one batch. `send` flips `busy` back
  // on synchronously (upload or stream start); `sending` covers the render in
  // between so a second entry cannot slip out on the same idle. State, not a
  // ref: a failed send drops `busy` before the flag resets, and only a state
  // change re-runs this for whatever is still queued.
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (busy || sending) return;
    const next = queue.find((m) => m.conversationId === conversationId);
    if (!next || next.id === holdId) return;
    setSending(true);
    setQueue((prev) => prev.filter((m) => m.id !== next.id));
    void send(next.text, next.annotations).finally(() => setSending(false));
  }, [busy, sending, queue, conversationId, holdId, send]);

  return {
    /** Entries for the current conversation, oldest first. */
    queued: queue.filter((m) => m.conversationId === conversationId),
    enqueue,
    remove,
    update,
    clear,
  };
}
